import { Badge, DataTable, type DataTableColumn } from "@design";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DeploymentResponse } from "@/api";
import { Text } from "@/components/Text";
import { formatNumber } from "@/i18n/format";

function Plain({ value }: Readonly<{ value?: string }>) {
  return (
    <Text as="span" variant="body-sm" tone="secondary">
      {value || "—"}
    </Text>
  );
}

/** Each deployment's model, SKU and the content filter policy it runs under. */
export function DeploymentTable({
  deployments,
  policyNames,
}: Readonly<{ deployments: DeploymentResponse[]; policyNames: Set<string> }>) {
  const { t } = useTranslation("adminGovernance");
  const columns: DataTableColumn<DeploymentResponse>[] = [
    {
      key: "name",
      header: t("deployments.columns.name"),
      mobile: "primary",
      render: (dep) => (
        <div className="flex min-w-0 flex-col text-left">
          <Text as="span" variant="body-sm" className="truncate">
            {dep.name}
          </Text>
          {dep.modelFormat && (
            <Text as="span" variant="caption" tone="tertiary">
              {dep.modelFormat}
            </Text>
          )}
        </div>
      ),
    },
    {
      key: "model",
      header: t("deployments.columns.model"),
      render: (dep) => <Plain value={[dep.modelName, dep.modelVersion].filter(Boolean).join(" ")} />,
    },
    {
      key: "provider",
      header: t("deployments.columns.provider"),
      render: (dep) => <Badge variant="neutral">{dep.provider || t("deployments.defaultProvider")}</Badge>,
    },
    { key: "sku", header: t("deployments.columns.sku"), render: (dep) => <Plain value={dep.skuName} /> },
    {
      key: "capacity",
      header: t("deployments.columns.capacity"),
      align: "right",
      render: (dep) => <Plain value={dep.skuCapacity ? formatNumber(dep.skuCapacity) : undefined} />,
    },
    {
      key: "policy",
      header: t("deployments.columns.policy"),
      render: (dep) => (
        <Badge
          variant={dep.raiPolicyName && policyNames.has(dep.raiPolicyName) ? "info" : "neutral"}
          icon={<ShieldCheck size={12} />}
        >
          {dep.raiPolicyName || t("deployments.none")}
        </Badge>
      ),
    },
    {
      key: "status",
      header: t("deployments.columns.status"),
      render: (dep) =>
        dep.provisioningState ? (
          <Badge variant={dep.provisioningState === "Succeeded" ? "success" : "warning"}>{dep.provisioningState}</Badge>
        ) : (
          <Plain />
        ),
    },
  ];
  return (
    <DataTable
      aria-label={t("deployments.tableLabel")}
      columns={columns}
      rows={deployments}
      rowKey={(dep) => `${dep.provider ?? ""}-${dep.name}`}
      empty={
        <Text variant="body-sm" tone="tertiary">
          {t("deployments.empty")}
        </Text>
      }
    />
  );
}
